// pages/Memoir.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { useToast } from '../../../context/ToastContext';
import {
  RememberBackground,
  Kapta_memior_book,
  Memior_video,
  How_it_Works1,
  How_it_Works2,
  How_it_Works3,
  Whats_included,
  RightQuestions
} from '../../../public/images/memoir';
import { ArrowDown, CheckIcon } from '../../../public/icons';
import { memoirService } from '../../../services/memoirService';

const Memoir = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { openModal } = useModal();
  const toast = useToast();

  const [openingDashboard, setOpeningDashboard] = useState(false);

  const handleCreateMemoir = (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please log in to create a memoir');
      openModal('signin');
      return;
    }
    navigate('/memoirs/create-memoir');
  };

  const handleViewDashboard = async (e) => {
    e.preventDefault();
    setOpeningDashboard(true);
    try {
      const res = await memoirService.getMyMemoirs();
      const memoirs = Array.isArray(res?.data) ? res.data : [];

      if (memoirs.length === 0) {
        /* No memoirs yet — send them to the create flow */
        navigate('/memoirs/create-memoir');
        return;
      }

      /* Navigate to the first (most recent) memoir */
      const firstId = memoirs[0].id;
      navigate(`/user/memoir/${firstId}`);
    } catch (err) {
      console.error('Failed to open dashboard:', err);
      navigate('/memoirs/create-memoir');
    } finally {
      setOpeningDashboard(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="memoir-hero min-h-[70vh] sm:min-h-[80vh] md:min-h-[90vh] bg-[#1C2A46] flex items-center py-12 sm:py-16 md:py-20"
        style={{
          backgroundImage: `url(${RememberBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="relative z-10 container flex justify-center items-center mx-auto px-4 sm:px-6 max-w-7xl mt-8 sm:mt-12 md:mt-16">
          <div className="max-w-7xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6 md:mb-8 text-center">
              Your Story,
              <br />
              <span className="text-white">Preserved Forever.</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 max-w-4xl leading-relaxed text-center px-2">
              Memoirs by Kapta is a guided way to document your life journey,
              your struggles, your becoming, your faith, your victories and
              turn it into a digital legacy book you can keep, revisit, and
              share.
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4 mt-6 sm:mt-8 justify-center">
              <button
                onClick={handleCreateMemoir}
                className="px-6 sm:px-8 py-2.5 sm:py-3 bg-white text-[#1C2A46] font-medium hover:bg-gray-100 transition-colors text-sm sm:text-base"
              >
                Create your Memoir
              </button>

              {isAuthenticated && (
                <button
                  onClick={handleViewDashboard}
                  disabled={openingDashboard}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 bg-white text-[#1C2A46] font-medium hover:bg-gray-100 transition-colors text-sm sm:text-base disabled:opacity-60 disabled:cursor-wait"
                >
                  {openingDashboard ? 'Opening…' : 'View Dashboard'}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Infinity Scrolling Text */}
      <InfinityScrollingText />

      {/* Video Section */}
      <div className="w-full">
        <img src={Memior_video} alt="Memoir video" className="w-full h-auto" />
      </div>

      {/* Overview Section */}
      <div className="bg-[#F7F5EF] py-12 sm:py-16 md:py-20 px-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-bold text-[#1C2A46] text-center px-2">
          Overview of Kapta’s Memoir Book
        </h1>
        <img
          src={Kapta_memior_book}
          className="w-full max-w-[1454.42px] h-auto mx-auto mt-6 sm:mt-8 md:mt-10"
          alt="Kapta memoir book"
        />
      </div>

      {/* How It Works Section */}
      <section className="bg-[#FCF7EE] py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-bold text-secondary mb-3 sm:mb-4 md:mb-5 flex justify-center items-center gap-2">
                How Kapta Memoir Works
              </h2>
              <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto font-normal text-[#6B635D] px-2">
                Guided storytelling that helps you reflect, document, and
                preserve the moments that shaped you.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8 lg:gap-10">
              <div className="w-full md:w-1/3">
                <div className="flex flex-col">
                  <div className="mb-3 sm:mb-4">
                    <img
                      src={How_it_Works1}
                      alt="Memory Magnet"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                  <p className="text-xs sm:text-sm text-left font-medium text-[#55000C] mb-1">
                    Step 1
                  </p>
                  <h3 className="text-lg sm:text-xl font-medium text-secondary mb-1 text-left">
                    Choose Your Memoir Path
                  </h3>
                  <p className="text-[#6B635D] text-sm sm:text-base font-light text-left">
                    Every journey is different. Start by choosing what your
                    memoir is about so we can guide you with the right
                    questions.
                  </p>
                </div>
              </div>

              <div className="w-full md:w-1/3">
                <div className="flex flex-col">
                  <div className="mb-3 sm:mb-4">
                    <img
                      src={How_it_Works2}
                      alt="Frame it Forward"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                  <p className="text-xs sm:text-sm text-left font-medium text-[#55000C] mb-1">
                    Step 2
                  </p>
                  <h3 className="text-lg sm:text-xl font-medium text-secondary mb-1 text-left">
                    Build Your Living Legacy Book
                  </h3>
                  <p className="text-[#6B635D] text-sm sm:text-base font-light text-left">
                    Every journey is different. Start by choosing what your
                    memoir is about so we can guide you with the right
                    questions.
                  </p>
                </div>
              </div>

              <div className="w-full md:w-1/3">
                <div className="flex flex-col">
                  <div className="mb-3 sm:mb-4">
                    <img
                      src={How_it_Works3}
                      alt="Legacy Magnet"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                  <p className="text-xs sm:text-sm text-left font-medium text-[#55000C] mb-1">
                    Step 3
                  </p>
                  <h3 className="text-lg sm:text-xl font-medium text-secondary mb-1 text-left">
                    Keep or Print Your Story
                  </h3>
                  <p className="text-[#6B635D] text-sm sm:text-base font-light text-left">
                    Every journey is different. Start by choosing what your
                    memoir is about so we can guide you with the right
                    questions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="min-h-[500px] sm:min-h-[600px] md:h-[804px]">
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-[60%] order-2 md:order-1">
            <img
              src={Whats_included}
              alt="Family reading memoir book together"
              className="w-full h-[300px] sm:h-[400px] md:h-[804px] object-cover"
            />
          </div>

          <div className="bg-[#1C2A46] p-6 sm:p-8 md:p-12 lg:p-20 w-full md:w-[40%] order-1 md:order-2 min-h-[400px] md:h-[804px] text-left overflow-y-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
              What's Included
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8">
              Everything you need to create a keepsake memoir
            </p>

            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 mt-1">
                  <CheckIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                </div>
                <span className="text-base sm:text-lg text-white font-light">
                  Write stories at your pace
                </span>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 mt-1">
                  <CheckIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                </div>
                <span className="text-base sm:text-lg text-white font-light">
                  Unlimited Photos and stories
                </span>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 mt-1">
                  <CheckIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                </div>
                <span className="text-base sm:text-lg text-white font-light">
                  Guided questions to help you through
                </span>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 mt-1">
                  <CheckIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                </div>
                <span className="text-base sm:text-lg text-white font-light">
                  Professional layout and design
                </span>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 md:mt-10">
              {isAuthenticated ? (
                <button
                  onClick={handleViewDashboard}
                  disabled={openingDashboard}
                  className="inline-block w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-[#1C2A46] font-semibold hover:bg-gray-100 transition-colors text-center disabled:opacity-60 disabled:cursor-wait"
                >
                  {openingDashboard ? 'Opening…' : 'View Dashboard'}
                </button>
              ) : (
                <button
                  onClick={handleCreateMemoir}
                  className="inline-block w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-[#1C2A46] font-semibold hover:bg-gray-100 transition-colors text-center"
                >
                  Create your Memoir
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Right Questions Image */}
      <div className="w-full">
        <img
          src={RightQuestions}
          alt="Right questions section"
          className="w-full h-auto"
        />
      </div>
    </div>
  );
};

export default Memoir;

function InfinityScrollingText() {
  return (
    <div className="bg-[#FCF7EE] py-4 sm:py-6 overflow-hidden">
      <div className="relative flex">
        <div className="flex animate-scroll whitespace-nowrap">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center">
              <span className="text-slate-800 text-2xl sm:text-3xl md:text-4xl mx-4 sm:mx-6 md:mx-8 font-light whitespace-nowrap">
                For Yourself
              </span>
              <span className="text-[#55000C] mx-2 sm:mx-3 md:mx-4 text-lg sm:text-xl md:text-2xl">
                •
              </span>
              <span className="text-slate-800 text-2xl sm:text-3xl md:text-4xl mx-4 sm:mx-6 md:mx-8 font-light whitespace-nowrap">
                For Family
              </span>
              <span className="text-[#55000C] mx-2 sm:mx-3 md:mx-4 text-lg sm:text-xl md:text-2xl">
                •
              </span>
              <span className="text-slate-800 text-2xl sm:text-3xl md:text-4xl mx-4 sm:mx-6 md:mx-8 font-light whitespace-nowrap">
                For Love
              </span>
              <span className="text-[#55000C] mx-2 sm:mx-3 md:mx-4 text-lg sm:text-xl md:text-2xl">
                •
              </span>
              <span className="text-slate-800 text-2xl sm:text-3xl md:text-4xl mx-4 sm:mx-6 md:mx-8 font-light whitespace-nowrap">
                For Legacy
              </span>
              <span className="text-[#55000C] mx-2 sm:mx-3 md:mx-4 text-lg sm:text-xl md:text-2xl">
                •
              </span>
            </div>
          ))}
        </div>

        <div
          className="flex animate-scroll whitespace-nowrap"
          aria-hidden="true"
        >
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center">
              <span className="text-slate-800 text-2xl sm:text-3xl md:text-4xl mx-4 sm:mx-6 md:mx-8 font-light whitespace-nowrap">
                For Yourself
              </span>
              <span className="text-[#55000C] mx-2 sm:mx-3 md:mx-4 text-lg sm:text-xl md:text-2xl">
                •
              </span>
              <span className="text-slate-800 text-2xl sm:text-3xl md:text-4xl mx-4 sm:mx-6 md:mx-8 font-light whitespace-nowrap">
                For Family
              </span>
              <span className="text-[#55000C] mx-2 sm:mx-3 md:mx-4 text-lg sm:text-xl md:text-2xl">
                •
              </span>
              <span className="text-slate-800 text-2xl sm:text-3xl md:text-4xl mx-4 sm:mx-6 md:mx-8 font-light whitespace-nowrap">
                For Love
              </span>
              <span className="text-[#55000C] mx-2 sm:mx-3 md:mx-4 text-lg sm:text-xl md:text-2xl">
                •
              </span>
              <span className="text-slate-800 text-2xl sm:text-3xl md:text-4xl mx-4 sm:mx-6 md:mx-8 font-light whitespace-nowrap">
                For Legacy
              </span>
              <span className="text-[#55000C] mx-2 sm:mx-3 md:mx-4 text-lg sm:text-xl md:text-2xl">
                •
              </span>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-scroll {
          animation: scroll 20s linear infinite;
        }

        @media (max-width: 640px) {
          .animate-scroll {
            animation-duration: 15s;
          }
        }
      `}</style>
    </div>
  );
}